import { useContext, useMemo, useCallback } from "react";
import { useQuery } from "@apollo/client";
import { GET_LEAD_SOURCES_DS } from "../graphql/query";
import { UserContext } from "../amplify/authenticator-provider";

const getLocationIdsFromSession = () => {
  const raw = sessionStorage?.getItem("currentLocationId") || "";
  return raw
    .split(",")
    .filter(Boolean)
    .map(id => +id)
    .filter(id => !Number.isNaN(id));
};

const normalize = s =>
  String(s || "")
    .trim()
    .toLowerCase();

/**
 * Walk the 3-level `getLeadSources` tree and return ALL descendant ids
 * (parents + leaves) under the node whose `name` matches `targetName`.
 * Match is case-insensitive and trimmed. Returns [] if not found.
 */
// Recursive find using reduce so we don't trip the no-restricted-syntax lint
// (no for-of). reduce short-circuits via the accumulator: once a match is
// found the remaining iterations just pass it through.
const findNodeByName = (nodes, target) =>
  (nodes || []).reduce((found, n) => {
    if (found) return found;
    if (normalize(n?.name) === target) return n;
    return findNodeByName(n?.parents, target) || findNodeByName(n?.childs, target) || null;
  }, null);

const findNodeById = (nodes, targetId) =>
  (nodes || []).reduce((found, n) => {
    if (found) return found;
    if (n?.key != null && +n.key === +targetId) return n;
    return findNodeById(n?.parents, targetId) || findNodeById(n?.childs, targetId) || null;
  }, null);

const collectAllDescendantKeys = node => {
  if (!node) return [];
  const acc = [];
  const walk = current => {
    (current.parents || []).forEach(p => {
      if (p?.key != null) acc.push(+p.key);
      walk(p);
    });
    (current.childs || []).forEach(c => {
      if (c?.key != null) acc.push(+c.key);
      walk(c);
    });
  };
  walk(node);
  return acc;
};

const collectDescendantIds = (roots, targetName) => {
  const target = normalize(targetName);
  if (!target || !Array.isArray(roots)) return [];

  const collect = (node, acc) => {
    if (!node) return;
    (node.parents || []).forEach(p => {
      if (p?.key != null) acc.push(+p.key);
      collect(p, acc);
    });
    (node.childs || []).forEach(c => {
      if (c?.key != null) acc.push(+c.key);
      collect(c, acc);
    });
  };

  const node = findNodeByName(roots, target);
  if (!node) return [];
  const ids = [];
  collect(node, ids);
  return ids;
};

/**
 * Hook around `getLeadSources` (a.k.a. opportunity sources). Mirrors the data
 * fetch used by OpportunitySourceSelect (company + session location scope,
 * cache-first) so the cache is shared across consumers.
 *
 * @param {Object}  [options]
 * @param {boolean} [options.skip]
 * @param {string}  [options.fetchPolicy="cache-first"]
 *
 * @returns {{
 *   sources: any[],
 *   data: any,
 *   loading: boolean,
 *   error: any,
 *   refetch: Function,
 *   getChildIdsByName: (name: string) => number[],
 *   getIdsWithChildrenByName: (name: string) => number[],
 *   findSourceByName: (name: string) => any | null,
 * }}
 */
const useOpportunitySources = (options = {}) => {
  const { skip = false, fetchPolicy = "cache-first" } = options;
  const { user } = useContext(UserContext);
  const companyId = user?.company?.id ? +user.company.id : null;

  const locationIds = useMemo(() => getLocationIdsFromSession(), []);

  const { data, loading, error, refetch } = useQuery(GET_LEAD_SOURCES_DS, {
    variables: { companyId, locationIds },
    skip: skip || !companyId || locationIds.length === 0,
    fetchPolicy,
  });

  const sources = data?.getLeadSources || [];

  // Pass a source name → get array of its descendant ids only (no self).
  const getChildIdsByName = useCallback(name => collectDescendantIds(sources, name), [sources]);

  // Pass a source name → get [self id, ...descendant ids].
  const getIdsWithChildrenByName = useCallback(
    name => {
      const target = normalize(name);
      if (!target) return [];
      const node = findNodeByName(sources, target);
      if (!node) return [];
      return [+node.key, ...collectDescendantIds(sources, name)];
    },
    [sources]
  );

  // Pass a source id → get [self id, ...descendant ids]. Mirrors the
  // name-keyed helper but for callers that already have the numeric id
  // (e.g. dashboard "By Source" drill-ins).
  const getIdsWithChildrenById = useCallback(
    id => {
      if (id == null) return [];
      const node = findNodeById(sources, id);
      if (!node) return [];
      return [+node.key, ...collectAllDescendantKeys(node)];
    },
    [sources]
  );

  const findSourceByName = useCallback(
    name => {
      const target = normalize(name);
      if (!target) return null;
      return findNodeByName(sources, target);
    },
    [sources]
  );

  return {
    sources,
    data,
    loading,
    error,
    refetch,
    getChildIdsByName,
    getIdsWithChildrenByName,
    getIdsWithChildrenById,
    findSourceByName,
  };
};

export default useOpportunitySources;
